/**
 * Shop the Look — "Add the look", ON-PAGE (no redirect). Custom Padel USA.
 * <shop-the-look-add> wraps the racket's matched set (sections/shop-the-look_custom.liquid).
 *
 * Adds every item in the look (the racket + its bag / shoes) as separate cart lines in one request,
 * then refreshes + opens the cart drawer WITHOUT navigating — mirroring the theme's product-form.js
 * batch add: POST /cart/add.js with the cart-items section ids, then dispatch CartAddEvent({sections})
 * which cart-drawer.js (auto-open) + component-cart-items.js listen to. Source 'shop-the-look' so the
 * kit-upsell popup (which gates on 'product-form') does NOT fire. Deduped against the live cart.
 * Choice groups (Sam, 2026-10-13): several bags from one collection, or several rackets on a look item's
 * page (bag, shoes), are alternatives — a select [data-stl-group-choice="bag|racket"] (lead preselected)
 * decides which [data-stl-group] card is added, never two of a kind. On an item page the anchor line is
 * tagged `pdp-look-anchor` (data-anchor-placement) and the chosen racket counts as a companion.
 * Items with a size get a select in the add row ([data-stl-choice][data-required]); the look is never
 * added with a guessed size — a missing size shows a field-level error (DESIGN.md §7.3).
 * Line property _added_from: pdp-look on the companions; the racket carries pdp-look-racket so a racket
 * bought through the look is never counted as upsell revenue.
 *
 * Analytics (upsell ranking — same contract as assets/build-your-kit.js): `shop_the_look_shown` once,
 * when the cards are a quarter on screen (companions as {slot, product_id, variant_id, price_cents,
 * position} + racket_product_id), and `shop_the_look_added` after a successful add that included a
 * companion (+ racket_added). dataLayer + document CustomEvent + Shopify.analytics.publish.
 *
 * Docs: custom-code-padelusa/features/shop-the-look.md
 */
import { CartAddEvent } from '@theme/events';

if (!customElements.get('shop-the-look-add')) {
  const CART_ADD_URL = window.Theme?.routes?.cart_add_url || '/cart/add.js';
  const CART_JS_URL = `${window.Theme?.routes?.cart_url || '/cart'}.js`;
  const SOURCE = 'shop-the-look';
  /** Line-item `_added_from` values; PLACEMENT is also the analytics `placement` label. */
  const PLACEMENT = 'pdp-look';
  const ANCHOR_PLACEMENT = 'pdp-look-racket';

  customElements.define(
    'shop-the-look-add',
    class extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-stl-add]');
        this.totalEl = this.querySelector('[data-stl-total]');
        if (!this.button) return;

        this.format = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: this.dataset.currency || 'USD',
        });
        this.#refreshTotal();
        this.button.addEventListener('click', (event) => this.#addLook(event));
        this.querySelectorAll('[data-stl-group-choice]').forEach((select) =>
          select.addEventListener('change', () => this.#refreshTotal())
        );
        this.querySelectorAll('[data-stl-choice]').forEach((select) =>
          select.addEventListener('change', () => {
            this.#setInvalid(select, false);
            this.#refreshTotal();
          })
        );
        this.#watchShown();
      }

      disconnectedCallback() {
        this.shownObserver?.disconnect();
      }

      /** One entry per product in the look (the carousel may clone slides, so dedupe by product id). */
      #items() {
        const seen = new Set();
        return [...this.querySelectorAll('[data-stl-item]')].filter((el) => {
          const id = el.dataset.productId;
          if (!id || seen.has(id)) return false;
          seen.add(id);
          return true;
        });
      }

      /** The select for a choice group ('racket' or 'bag'): alternatives of one kind, the shopper adds one. */
      #groupChoice(group) {
        return this.querySelector(`[data-stl-group-choice="${group}"]`);
      }

      /** Items that go into the cart: every card, except grouped ones other than their group's choice. */
      #chosenItems() {
        return this.#items().filter((item) => {
          const group = item.dataset.stlGroup;
          if (!group) return true;
          return item.dataset.productId === this.#groupChoice(group)?.selectedOptions[0]?.dataset.productId;
        });
      }

      #choiceFor(item) {
        return this.querySelector(`[data-stl-choice][data-product-id="${item.dataset.productId}"]`);
      }

      /** The variant + price to add for an item: its select when it has one, else the card default. */
      #pick(item) {
        if (item.dataset.stlGroup) {
          const option = this.#groupChoice(item.dataset.stlGroup)?.selectedOptions[0];
          return {
            variantId: option?.value || item.dataset.variantId || '',
            priceCents: Number(option?.dataset.priceCents) || Number(item.dataset.priceCents) || 0,
          };
        }
        const select = this.#choiceFor(item);
        if (select) {
          const option = select.selectedOptions[0];
          return {
            variantId: select.value || '',
            priceCents: Number(option?.dataset.priceCents) || Number(item.dataset.priceCents) || 0,
          };
        }
        return { variantId: item.dataset.variantId || '', priceCents: Number(item.dataset.priceCents) || 0 };
      }

      #isAnchor(item) {
        return item.hasAttribute('data-stl-anchor');
      }

      #companions() {
        return this.#chosenItems().filter((item) => !this.#isAnchor(item));
      }

      #cardInfo(item) {
        const { variantId, priceCents } = this.#pick(item);
        return {
          slot: item.dataset.stlSlot || '',
          product_id: item.dataset.productId || '',
          variant_id: variantId,
          price_cents: priceCents,
          position: this.#companions().indexOf(item) + 1,
        };
      }

      #anchorProductId() {
        return this.#items().find((item) => this.#isAnchor(item))?.dataset.productId || '';
      }

      /** The racket of the look: the anchor on a racket page, the chosen racket on an item page. */
      #racketProductId() {
        return this.#chosenItems().find((item) => item.dataset.stlSlot === 'racket')?.dataset.productId || '';
      }

      /** Impression = the look is at least a quarter on screen (it sits low on the PDP). */
      #watchShown() {
        if (this.shownTracked || typeof IntersectionObserver === 'undefined') return;
        this.shownObserver = new IntersectionObserver(
          (entries) => {
            if (this.shownTracked || !entries.some((entry) => entry.isIntersecting)) return;
            this.shownTracked = true;
            this.shownObserver.disconnect();
            this.#track('shop_the_look_shown', {
              racket_product_id: this.#racketProductId(),
              anchor_product_id: this.#anchorProductId(),
              mode: this.dataset.mode || 'racket',
              items: this.#companions().map((item) => this.#cardInfo(item)),
            });
          },
          { threshold: 0.25 }
        );
        this.shownObserver.observe(this);
      }

      /** Fire-and-forget analytics (same contract as assets/build-your-kit.js #track) — never throws. */
      #track(name, detail) {
        try {
          const payload = { ...detail, placement: PLACEMENT };
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: name, ...payload });
          document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: payload }));
          window.Shopify?.analytics?.publish?.(name, payload);
        } catch (error) {
          /* analytics must never break the look */
        }
      }

      #refreshTotal() {
        const cents = this.#chosenItems().reduce((sum, item) => sum + this.#pick(item).priceCents, 0);
        if (this.totalEl) this.totalEl.textContent = cents ? this.format.format(cents / 100) : '';
      }

      #setInvalid(select, invalid) {
        const field = select.closest('[data-stl-field]');
        const error = field?.querySelector('[data-stl-error]');
        if (invalid) {
          field?.setAttribute('data-invalid', '');
          select.setAttribute('aria-invalid', 'true');
          if (error) {
            error.hidden = false;
            error.id = error.id || `${select.id}-error`;
            select.setAttribute('aria-describedby', error.id);
          }
        } else {
          field?.removeAttribute('data-invalid');
          select.removeAttribute('aria-invalid');
          if (error) error.hidden = true;
        }
      }

      /** Every required size chosen? Marks the missing ones; focus moves only for keyboard activation. */
      #validate(event) {
        const missing = [...this.querySelectorAll('[data-stl-choice][data-required]')].filter((s) => !s.value);
        missing.forEach((select) => this.#setInvalid(select, true));
        if (missing.length && event?.detail === 0) missing[0].focus();
        return missing.length === 0;
      }

      async #getCart() {
        try {
          const res = await fetch(CART_JS_URL, { headers: { Accept: 'application/json' } });
          return res.ok ? await res.json() : undefined;
        } catch (error) {
          return undefined;
        }
      }

      /** cart-items-component section ids — so /cart/add.js returns re-rendered cart HTML for the drawer. */
      #cartSectionIds() {
        const ids = new Set();
        document.querySelectorAll('cart-items-component').forEach((el) => {
          if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
        });
        return [...ids];
      }

      async #addLook(event) {
        if (!this.#validate(event)) return;
        this.button.disabled = true;
        this.button.setAttribute('aria-busy', 'true');

        const cart = await this.#getCart();
        const inCart = new Set((cart?.items || []).map((line) => String(line.variant_id)));
        const lines = [];
        const added = [];
        this.#chosenItems().forEach((item) => {
          const { variantId } = this.#pick(item);
          if (!variantId || inCart.has(String(variantId))) return;
          const anchor = this.#isAnchor(item);
          lines.push({
            id: Number(variantId),
            quantity: 1,
            properties: { _added_from: anchor ? this.dataset.anchorPlacement || ANCHOR_PLACEMENT : PLACEMENT },
          });
          if (!anchor) added.push(item);
        });

        // Whole look already in the cart → just open the drawer (drawer stores), no add.
        if (lines.length === 0) {
          document.dispatchEvent(new CartAddEvent({}, SOURCE, { source: SOURCE }));
          this.#showStatus();
          this.#reset();
          return;
        }

        try {
          const response = await fetch(CART_ADD_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: lines, sections: this.#cartSectionIds().join(',') }),
          }).then((r) => r.json());

          if (response.status) {
            // Shopify error (e.g. just sold out) — leave the button usable; the cart is server truth.
            this.#reset();
            return;
          }

          if (added.length) {
            this.#track('shop_the_look_added', {
              racket_product_id: this.#racketProductId(),
              anchor_product_id: this.#anchorProductId(),
              mode: this.dataset.mode || 'racket',
              anchor_added: lines.length > added.length,
              racket_added:
                (this.dataset.mode || 'racket') === 'racket'
                  ? lines.length > added.length
                  : added.some((item) => item.dataset.stlSlot === 'racket'),
              items: added.map((item) => this.#cardInfo(item)),
            });
          }
          const updated = await this.#getCart();
          document.dispatchEvent(
            new CartAddEvent(updated ?? undefined, SOURCE, {
              source: SOURCE,
              itemCount: lines.length,
              sections: response.sections,
            })
          );
          this.#showStatus();
        } catch (error) {
          /* no-op: cart is server-truth on next load */
        }
        this.#reset();
      }

      /** Inline confirmation — with the cart page setting no drawer opens, the button alone says nothing. */
      #showStatus() {
        const status = this.querySelector('[data-stl-status]');
        if (status) status.hidden = false;
      }

      #reset() {
        this.button.disabled = false;
        this.button.removeAttribute('aria-busy');
      }
    }
  );
}
